import { type PowerSyncDrizzleDB, user } from "@cashsync/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (locale, global theme, blur, follow-device) had a guest branch
 * that skipped the filter:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * In guest mode, the `await` landed on the builder WITHOUT `.where(...)`, and
 * Drizzle's builder is await-able on its own: that executes an
 * `UPDATE "user" SET …` on ALL rows in the local table. It slipped in because
 * the block of `eslint.config.js` that enables
 * `drizzle/enforce-update-with-where` pointed with `files:` at `packages/db` and
 * `packages/api`, two packages that do not exist in this repo — the rule had
 * been green for months without anyone inspecting a single line.
 *
 * The premise supporting the guest branch —"the guest database has only one
 * `user` row, so an unfiltered UPDATE is equivalent"— is false during a
 * guest→real upgrade: there the guest database CONNECTS to the real account's
 * connector, which self-subscribes to `organizations` and downloads one `user`
 * row for EACH co-member. `user` is not `localOnly`, so every touched row also
 * queues its own upload op.
 *
 * The filter was not guessed: `ThemeProvider` seeds the guest row
 * (`apps/web/src/providers/theme-provider.tsx`) with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME row that the
 * unfiltered branch intended to touch when the table had only one row —
 * identical behavior in the normal case, bounded in the rest. It is also the
 * form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, which never had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── Why they all go through `patchRowVerified` (2026-08-13) ───────────────────
 * `user` had been excluded from the PowerSync window by copying the canon
 * ("the server creates the row at registration, before the first preference").
 * An independent audit (`infra/reports/user-exclusion-audit/
 * 2026-08-13-summary.md`) checked the argument against the real code and found
 * it FALSE: `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) seeds
 * this local row for ANY `uid` —it does not distinguish guest from real
 * account— without waiting for `status().hasSynced`, and `userId()` activates
 * from `signUpActor`'s REST response, independently of the sync stream. There
 * is a window; these 6 mutations **can throw** (`LocalWriteLostError`) — the
 * caller must communicate it, never swallow it (all of them do today).
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) and not through
 * REST: the new photo appears immediately and survives a cold reload without a
 * connection. The BINARY does go through REST (`POST /api/avatar`) because it
 * does not fit in the sync queue; only the key returned by that upload travels
 * through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
